import { TestId } from '@shared-core/libs/test-ids'
import { normalizeOtpCode } from '@shared-core/utils/otp'
import { InputCursor } from '@shared-ui/components/ui/inputs/cursor'
import { GTextAnimated } from '@shared-ui/components/ui/text'
import { type ComponentRef, forwardRef, useImperativeHandle, useRef } from 'react'
import { TextInput, View } from 'react-native'
import { ZoomIn } from 'react-native-reanimated'
import { useCreateStyles } from './styles'

type CodeInputProps = {
  length: number
  value: string
  onChange: (val: string) => void
  isNumeric?: boolean
  isError?: boolean
  isSuccess?: boolean
  variant?: 'default' | 'entry-desktop'
}

export type ConfirmCodeInputRef = {
  focus: () => void
  blur: () => void
}

export const AppConfirmCodeInput = forwardRef<ConfirmCodeInputRef, CodeInputProps>(
  function AppConfirmCodeInputComponent(props, ref) {
    const inputRef = useRef<ComponentRef<typeof TextInput>>(null)
    const isDesktop = props.variant === 'entry-desktop'
    const styles = useCreateStyles()

    useImperativeHandle(ref, () => ({
      focus: () => {
        const input = inputRef.current as unknown as HTMLInputElement | null
        input?.focus({ preventScroll: true })
      },
      blur: () => inputRef.current?.blur(),
    }))

    const handleChangeText = (text: string) => {
      props.onChange(normalizeOtpCode(text))
    }

    return (
      <View style={[styles.container, isDesktop && styles.containerDesktop]}>
        <TextInput
          ref={inputRef}
          style={styles.hiddenInputWeb}
          keyboardType={props.isNumeric ? 'number-pad' : 'default'}
          inputMode={props.isNumeric ? 'numeric' : 'text'}
          autoCapitalize="characters"
          autoCorrect={false}
          value={props.value}
          caretHidden
          maxLength={props.length}
          testID={TestId.AUTH_OTP_INPUT}
          onChangeText={handleChangeText}
          autoFocus={false}
        />

        {Array.from({ length: props.length }).map((_, i) => {
          const char = props.value[i] ?? ''
          const isFocused = i === props.value.length

          return (
            <View
              key={`input-code${i}`}
              style={[
                styles.cell,
                isDesktop && styles.cellDesktop,
                isFocused && styles.cellFocused,
                props.isError && styles.cellIsError,
                props.isSuccess && styles.cellIsSuccess,
                { pointerEvents: 'none' },
              ]}
            >
              {!char && isFocused && <InputCursor size={26} isVisible={true} />}
              {!!char && (
                <GTextAnimated
                  entering={ZoomIn.duration(200)}
                  style={[
                    styles.cellText,
                    isDesktop && styles.cellTextDesktop,
                    props.isError && styles.cellTextIsError,
                    props.isSuccess && styles.cellTextIsSuccess,
                  ]}
                >
                  {char}
                </GTextAnimated>
              )}
            </View>
          )
        })}
      </View>
    )
  },
)

AppConfirmCodeInput.displayName = 'AppConfirmCodeInput'
